import { type SanityGallery } from '@metmuseum/types';

const galleryNumberRegex = /^\d+/; // matches "123", "123A" and "123 North" but not "The Great Hall"

export const galleryIsNumeric = (gallery: SanityGallery): boolean => {
	return !!`${gallery.number}`.match(galleryNumberRegex);
};

export const galleryAsNumber = (gallery: SanityGallery): number | undefined => {
	const match = `${gallery.number}`.match(galleryNumberRegex);
	return match ? parseInt(match[0]) : undefined;
};

export const formatGalleryName = (gallery: SanityGallery): string => {
	const { name, number } = gallery;
	return name || `Gallery ${number}`;
};

export const formatGalleryNameShort = (gallery: SanityGallery): string => {
	if (/^\d+$/.test(gallery.number)) {
		return `Gallery ${gallery.number}`;
	} else {
		return gallery.name;
	}
};

export const formatGalleryNumberOrName = (
	gallery: SanityGallery,
	showGallery: boolean = true,
	isPlural: boolean = false,
): string => {
	const { name, number } = gallery;
	const galleryWord = isPlural ? 'Galleries' : 'Gallery';
	return galleryIsNumeric(gallery)
		? showGallery
			? `${galleryWord} ${number}`
			: number
		: name;
};

export const formatGalleryGroupArray = (
	groupedGalleries: Array<Array<SanityGallery>>,
	showFirstGalleryOnly: boolean = true,
): Array<string> => {
	let showGallery = true;
	const isPlural = numericGalleriesGroupsArePlural(groupedGalleries);
	return groupedGalleries
		.filter((group) => !!group.length)
		.map((group) => {
			const name = formatGalleryGroup(group, showGallery, isPlural);
			if (showFirstGalleryOnly && galleryIsNumeric(group[0])) {
				showGallery = false;
			}
			return name;
		});
};

export const formatGalleryGroup = (
	galleries: Array<SanityGallery>,
	showGallery: boolean = true,
	forcePlural: boolean = false,
): string => {
	const isActuallyPlural =
		forcePlural || numericGalleriesGroupsArePlural([galleries]);
	if (!galleries?.length) {
		return '';
	} else if (galleries.length == 1) {
		return formatGalleryNumberOrName(
			galleries[0],
			showGallery,
			isActuallyPlural,
		);
	} else {
		const galleryRange = `${galleries[0].number}–${galleries[galleries.length - 1].number}`;
		return showGallery ? `Galleries ${galleryRange}` : galleryRange;
	}
};

export const sortAndGroupGalleries = (
	galleries: Array<SanityGallery>,
): Array<Array<SanityGallery>> => {
	return groupGalleries(sortGalleries(galleries));
};

export const numericGalleriesGroupsArePlural = (
	galleryGroups: Array<Array<SanityGallery>>,
): boolean => {
	const filteredGalleries = galleryGroups.filter(
		(group) => !!group.length && galleryIsNumeric(group[0]),
	);
	return filteredGalleries.flat().length !== 1; // This is technically correct in English
};

export const sortGalleries = (galleries: Array<SanityGallery>) =>
	galleries.sort((a, b) => {
		return gallerySortFunction(a, b);
	});

export const gallerySortFunction = (a: SanityGallery, b: SanityGallery) => {
	const isANumeric = galleryIsNumeric(a);
	const isBNumeric = galleryIsNumeric(b);

	// Ensure alphanumeric slugs are sorted before number
	if (isANumeric && !isBNumeric) {
		return 1;
	} else if (!isANumeric && isBNumeric) {
		return -1;
	}
	// If both are numbers do the normal numeric ordering
	else if (isANumeric) {
		return galleryAsNumber(a) - galleryAsNumber(b);
	}
	// If all else fails, order the galleries alphabeically by name
	else {
		const galleryA = a.name.startsWith('The') ? a.name.slice(4) : a.name;
		const galleryB = b.name.startsWith('The') ? b.name.slice(4) : b.name;
		return galleryA < galleryB ? -1 : 1;
	}
};

// Rolls galleries into groups. Adjacent galleries will be grouped together.
// Input: ["Gallery 1", "Gallery 3", "Gallery 4", "Gallery 5", "Gallery 7"]
// Output [["Gallery 1"], ["Gallery 3", "Gallery 4", "Gallery 5"], ["Gallery 7"]]
export const groupGalleries = (galleries) =>
	galleries?.reduce(
		(r: Array<Array<SanityGallery>>, gallery: SanityGallery) => {
			const lastSubArray = r[r.length - 1];

			// If the gallery has a non-numeric key or the number anything other than 1 greater than the last
			// element of the last array in the list, make a new group array.
			// Otherwise, if it's adjacent, use the existing group.
			// galleryAsNumber() can return undefined, so to make the left operand a number, -1 is added to it rather than + 1 subtracted
			if (
				!lastSubArray ||
				!galleryIsNumeric(gallery) ||
				galleryAsNumber(lastSubArray[lastSubArray.length - 1]) !==
					galleryAsNumber(gallery) - 1
			) {
				r.push([]);
			}

			// Push the gallery into the last array, whether already existing or we just made a new one.
			r[r.length - 1].push(gallery);
			return r;
		},
		[],
	);

const galleryRegex = /title="([^"]+)"/;
const numberRegex = /(\d{3})$/;

// To extract the gallery number from a tour or event in Elastic
// If the gallery name has a number in it, return the number. Otherwise, return empty string.
export const getGalleryNumberFromEventLocation = (htmlGunk: string): string => {
	const galleryMatches = htmlGunk?.match(galleryRegex);
	if (galleryMatches && galleryMatches[1]) {
		const galleryName = galleryMatches[1];
		const numberMatches = galleryName.match(numberRegex);
		return numberMatches ? numberMatches[1] : '';
	}
	return '';
};
